import * as React from 'react';
import {Icon, Upload} from 'antd';
import {UploadChangeParam} from 'antd/lib/upload';
import {HttpRequestHeader, UploadFile} from 'antd/lib/upload/interface';
import {mapAttachmentToUploadFile} from "./image";
import {Attachment, ErrorResponse} from "../../api/dtos/dtos";
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from "../../app/ApplicationContext";
import {getAuthorizedConfig} from "../../api/auth";
import {ErrorAlert} from "../ErrorAlert";
import locales from "../../locales";
import {showConfirm, showSaveErrorNotification, showSuccessNotification} from "../../utils/dialogs";

const {Dragger} = Upload;

interface Props {
  disabled: boolean;
  itemId: number;
  action?: string;
  loadAttachment: (id: number) => Promise<Attachment[]>;
  deleteAttachment: (id: number, attachmentId: number) => Promise<void>;
  onAttachmentChange?: () => void;
}

interface State {
  fileList: UploadFile[]
  error?: ErrorResponse;
}

class FileUploadEditor extends React.Component<Props, State> {
  public readonly loadingHandler?: LoadingOverlayHandler;
  public state: State = {
    fileList: [],
  };

  public componentDidMount() {
    this.loadAttachments();
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (prevProps.itemId !== this.props.itemId) {
      this.loadAttachments();
    }
  }

  public render() {
    const {disabled, action} = this.props;
    const {fileList, error} = this.state;
    const headers: HttpRequestHeader = getAuthorizedConfig().headers || {};

    const style = !disabled
      ? undefined
      : {color: '#bfbfbf'};

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>
        <Dragger
          name={"file"}
          action={action}
          headers={headers}
          disabled={disabled}
          fileList={fileList}
          multiple={true}
          onChange={this.onChange}
          showUploadList={{showRemoveIcon: !disabled}}
        >
          <p className="ant-upload-drag-icon"><Icon type="inbox"/></p>
          <p style={style}>{locales.file_upload_message}</p>
        </Dragger>
        <ErrorAlert style={{marginTop: '16px'}} error={error}/>
      </>
    );
  }

  private loadAttachments = () => {
    const promise = this.props.loadAttachment(this.props.itemId)
      .then(this.onAttachmentsLoaded)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onAttachmentsLoaded = (attachments: Attachment[]) => {
    this.setState({
      error: undefined,
      fileList: attachments.map(a => mapAttachmentToUploadFile(a))
    });
  };

  private onChange = (info: UploadChangeParam) => {
    const fileList = info.fileList.map(file => {
      const response = file.response as Partial<Attachment> | undefined;
      // Keep antd-generated uid for failed uploads; only replace it when backend returns a persisted attachment.
      if (file.status === 'done' && response && response.id !== undefined) {
        file.uid = String(response.id);
        file.name = response.name || file.name;
        file.url = response.url;
      }
      return file;
    });

    if (info.file.status === 'removed') {
      showConfirm(locales.file_delete_confirm, () => this.removeFile(info.file, fileList))

    } else if (info.file.status === 'done') {
      showSuccessNotification(locales.file_upload_success);
      this.setFileList(fileList);

    } else if (info.file.status === 'error') {
      showSaveErrorNotification();
      this.setFileList(fileList);
    } else {
      this.setFileList(fileList);
    }
  };

  private removeFile = (fileToDelete: UploadFile, remainingFileList: UploadFile[]) => {
    if (fileToDelete.error === undefined) {
      this.props.deleteAttachment(this.props.itemId, Number(fileToDelete.uid))
        .then(() => {
          this.setFileList(remainingFileList);
          showSuccessNotification(locales.file_delete_success);
          this.props.onAttachmentChange?.();
        })
        .catch((error: ErrorResponse) => {
          remainingFileList.push({
            ...fileToDelete,
            status: 'error',
            response: error.message
          });
          this.setFileList(remainingFileList);
        });
    } else {
      this.setFileList(remainingFileList);
    }
  };

  private setFileList = (fileList: UploadFile[]) => {
    this.setState({fileList});
  };

  private onApiError = (error: ErrorResponse) => this.setState({error});
}

export default FileUploadEditor;



// WEBPACK FOOTER //
// ./src/common/input/FileUploadEditor.tsx